import React, { useEffect, useRef, useState } from 'react';
import {
  Trophy,
  BadgeIndianRupee,
  Users,
  CalendarCheck,
  Layers,
  MapPin,
  ShieldCheck,
  TrendingUp,
  Activity,
  ArrowUpRight,
} from 'lucide-react';
import { Reveal, useSheen } from './Reveal';

const iconMap = {
  Trophy,
  BadgeIndianRupee,
  Users,
  CalendarCheck,
  Layers,
  MapPin,
  ShieldCheck,
  TrendingUp,
};

const colorMap = {
  amber: { ring: 'bg-amber-500/15', icon: 'text-amber-400', text: 'text-amber-400', dot: 'bg-amber-400' },
  blue: { ring: 'bg-blue-500/15', icon: 'text-blue-400', text: 'text-blue-400', dot: 'bg-blue-400' },
  green: { ring: 'bg-emerald-500/15', icon: 'text-emerald-400', text: 'text-emerald-400', dot: 'bg-emerald-400' },
  purple: { ring: 'bg-violet-500/15', icon: 'text-violet-400', text: 'text-violet-400', dot: 'bg-violet-400' },
};

// Animated comparison bar — fills when visible
const ComparisonBar = ({ value, max = 100, color = 'amber' }) => {
  const [w, setW] = useState(0);
  const ref = useRef(null);
  const started = useRef(false);

  useEffect(() => {
    const obs = new IntersectionObserver(
      (entries) => {
        entries.forEach((e) => {
          if (e.isIntersecting && !started.current) {
            started.current = true;
            const duration = 1600;
            const start = performance.now();
            const target = (value / max) * 100;
            const tick = (t) => {
              const p = Math.min((t - start) / duration, 1);
              const eased = 1 - Math.pow(1 - p, 3);
              setW(target * eased);
              if (p < 1) requestAnimationFrame(tick);
            };
            requestAnimationFrame(tick);
          }
        });
      },
      { threshold: 0.3 }
    );
    if (ref.current) obs.observe(ref.current);
    return () => obs.disconnect();
  }, [value, max]);

  const gradient =
    color === 'amber'
      ? 'linear-gradient(90deg, #F59E0B 0%, #FBBF24 100%)'
      : color === 'green'
      ? 'linear-gradient(90deg, #10B981 0%, #34D399 100%)'
      : 'linear-gradient(90deg, #3B82F6 0%, #60A5FA 100%)';

  return (
    <div ref={ref} className="relative h-2 rounded-full bg-white/5 overflow-hidden">
      <div
        className="absolute inset-y-0 left-0 rounded-full"
        style={{ width: `${w}%`, background: gradient, transition: 'width 80ms linear' }}
      />
    </div>
  );
};

// Vertical infinite-scroll feed of recent wins
const RecentWinsFeed = ({ wins }) => {
  // Duplicate for seamless loop
  const items = [...wins, ...wins];
  return (
    <div className="relative h-[360px] overflow-hidden">
      <div
        className="flex flex-col gap-3 animate-[scrollY_24s_linear_infinite]"
        style={{
          animationName: 'scrollY',
          animationDuration: `${wins.length * 3.2}s`,
          animationTimingFunction: 'linear',
          animationIterationCount: 'infinite',
        }}
      >
        {items.map((w, i) => {
          const c = colorMap[w.accent] || colorMap.amber;
          return (
            <div
              key={i}
              className="flex items-center gap-3 p-3.5 rounded-xl bg-white/[0.03] border border-white/5"
            >
              <span className={`w-9 h-9 rounded-lg ${c.ring} flex items-center justify-center flex-shrink-0`}>
                <ArrowUpRight className={`w-4 h-4 ${c.icon}`} />
              </span>
              <div className="flex-1 min-w-0">
                <div className="flex items-baseline gap-2">
                  <span className={`font-bold ${c.text}`}>{w.amount}</span>
                  <span className="text-xs text-slate-500">·</span>
                  <span className="text-xs text-slate-400 truncate">{w.when}</span>
                </div>
                <div className="text-xs text-slate-300 truncate">{w.sector}</div>
              </div>
            </div>
          );
        })}
      </div>
      {/* Fade overlays */}
      <div className="themed-feed-fade-t pointer-events-none absolute top-0 inset-x-0 h-10 bg-gradient-to-b from-[#0a0f1e] to-transparent" />
      <div className="themed-feed-fade-b pointer-events-none absolute bottom-0 inset-x-0 h-10 bg-gradient-to-t from-[#0a0f1e] to-transparent" />
    </div>
  );
};

const ImpactSnapshot = ({ data }) => {
  const { onMouseMove } = useSheen();
  const { focal, supporting, trustStrip, recentWins, sectors } = data;

  return (
    <section className="relative py-24 lg:py-32 px-6 lg:px-10 overflow-hidden">
      {/* Aurora blobs for premium ambience */}
      <div className="aurora-blob aurora-emerald aurora-animate-1" style={{ width: 500, height: 500, top: '-10%', right: '-10%', opacity: 0.3 }} />
      <div className="aurora-blob aurora-amber aurora-animate-3" style={{ width: 400, height: 400, bottom: '0%', left: '-5%', opacity: 0.25 }} />

      {/* Inject keyframes for vertical scroll */}
      <style>{`
        @keyframes scrollY {
          0% { transform: translateY(0); }
          100% { transform: translateY(-50%); }
        }
        @keyframes marqueeX {
          0% { transform: translateX(0); }
          100% { transform: translateX(-50%); }
        }
      `}</style>

      <div className="max-w-[1400px] mx-auto">
        {/* Header */}
        <Reveal className="text-center mb-14">
          <div className="section-num mb-5 justify-center">
            <span className="num">01</span>
            <span>Impact Snapshot</span>
          </div>
          <h2 className="font-display text-4xl sm:text-5xl lg:text-6xl font-extrabold tracking-tight leading-[1.05]">
            <span className="text-white">A Track Record Built for </span>
            <span className="text-gradient-mixed">Enterprise Procurement</span>
          </h2>
          <p className="mt-5 text-slate-400 max-w-2xl mx-auto leading-relaxed">
            Every figure below is drawn from a completed, verifiable engagement under NDA. No
            vanity metrics — only the outcomes a procurement committee or audit team would accept
            as evidence of a serious bid partner.
          </p>
        </Reveal>

        {/* Trust strip */}
        <Reveal delay={120} className="flex flex-wrap items-center justify-center gap-3 mb-12">
          {trustStrip.map((b, i) => {
            const Icon = iconMap[b.icon] || ShieldCheck;
            return (
              <div
                key={i}
                className="inline-flex items-center gap-2 px-4 py-2.5 rounded-full glass-card text-sm text-slate-200"
              >
                <Icon className="w-4 h-4 text-amber-400" />
                {b.label}
              </div>
            );
          })}
        </Reveal>

        {/* Main grid */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 items-start">
          {/* Focal card with comparison bar */}
          <Reveal variant="left" className="lg:col-span-2">
            <div
              onMouseMove={onMouseMove}
              className="card-sheen glass-card glass-card-hover rounded-3xl p-8 lg:p-10"
            >
              <div className="flex items-start gap-4 mb-6">
                <div className="w-12 h-12 rounded-xl bg-emerald-500/15 flex items-center justify-center">
                  <TrendingUp className="w-5 h-5 text-emerald-400" />
                </div>
                <div>
                  <div className="text-[11px] tracking-[0.2em] uppercase text-emerald-400 font-semibold mb-1">
                    Headline Metric
                  </div>
                  <div className="text-sm text-slate-300">{focal.label}</div>
                </div>
              </div>

              <div className="flex items-baseline gap-3 mb-7">
                <div className="text-7xl lg:text-8xl font-extrabold tracking-tight text-emerald-400">
                  {focal.metric}
                </div>
                <div className="text-base text-slate-400">
                  vs <span className="text-white font-semibold">{focal.industryAvg}%</span> industry avg
                </div>
              </div>

              <p className="text-slate-300 leading-relaxed mb-7 max-w-2xl">{focal.desc}</p>

              {/* Comparison bars */}
              <div className="space-y-4">
                <div>
                  <div className="flex items-center justify-between mb-1.5">
                    <span className="text-xs uppercase tracking-[0.15em] text-slate-400 font-semibold">
                      Arvanta
                    </span>
                    <span className="text-sm font-bold text-emerald-400">{focal.ours}%</span>
                  </div>
                  <ComparisonBar value={focal.ours} color="green" />
                </div>
                <div>
                  <div className="flex items-center justify-between mb-1.5">
                    <span className="text-xs uppercase tracking-[0.15em] text-slate-400 font-semibold">
                      Industry Average
                    </span>
                    <span className="text-sm font-bold text-slate-300">{focal.industryAvg}%</span>
                  </div>
                  <ComparisonBar value={focal.industryAvg} color="blue" />
                </div>
              </div>

              {/* Supporting mini-stats */}
              <div className="mt-9 pt-7 border-t border-white/5 grid grid-cols-1 sm:grid-cols-3 gap-5">
                {supporting.map((s, i) => {
                  const Icon = iconMap[s.icon] || Trophy;
                  const c = colorMap[s.color];
                  return (
                    <div key={i} className="flex items-start gap-3">
                      <div className={`w-10 h-10 rounded-lg ${c.ring} flex items-center justify-center flex-shrink-0`}>
                        <Icon className={`w-4 h-4 ${c.icon}`} />
                      </div>
                      <div>
                        <div className={`text-2xl font-extrabold tracking-tight ${c.text}`}>
                          {s.value}
                        </div>
                        <div className="text-sm text-white font-semibold">{s.label}</div>
                        <div className="text-xs text-slate-400">{s.sub}</div>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          </Reveal>

          {/* Recent Wins live feed */}
          <Reveal variant="right" delay={150}>
            <div
              onMouseMove={onMouseMove}
              className="card-sheen glass-card glass-card-hover rounded-3xl p-6 lg:p-7"
            >
              <div className="flex items-center justify-between mb-5">
                <div className="flex items-center gap-2">
                  <span className="relative flex w-2 h-2">
                    <span className="absolute inline-flex w-full h-full rounded-full bg-emerald-400 opacity-60 pulse-dot" />
                    <span className="relative inline-flex w-2 h-2 rounded-full bg-emerald-400" />
                  </span>
                  <span className="text-[11px] tracking-[0.2em] uppercase text-emerald-400 font-semibold">
                    Live · Recent Wins
                  </span>
                </div>
                <Activity className="w-4 h-4 text-slate-500" />
              </div>

              <RecentWinsFeed wins={recentWins} />

              <div className="mt-5 pt-4 border-t border-white/5 flex items-center justify-between">
                <span className="text-xs text-slate-400">Anonymized · NDA-protected</span>
                <span className="text-xs text-amber-400 font-semibold">
                  {recentWins.length}+ this quarter
                </span>
              </div>
            </div>
          </Reveal>
        </div>

        {/* Sector marquee */}
        <Reveal delay={200} className="mt-14">
          <div className="text-center mb-5">
            <span className="text-[11px] tracking-[0.25em] uppercase text-slate-400 font-semibold">
              Sectors We've Delivered Across
            </span>
          </div>
          <div className="relative overflow-hidden">
            <div
              className="flex gap-3 whitespace-nowrap"
              style={{
                animation: `marqueeX ${sectors.length * 4}s linear infinite`,
              }}
            >
              {[...sectors, ...sectors].map((s, i) => (
                <span
                  key={i}
                  className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full bg-white/[0.03] border border-white/10 text-sm text-slate-300 flex-shrink-0"
                >
                  <span className="w-1 h-1 rounded-full bg-amber-400" />
                  {s}
                </span>
              ))}
            </div>
            {/* fade edges */}
            <div className="themed-fade-l-edge pointer-events-none absolute left-0 top-0 bottom-0 w-24 bg-gradient-to-r from-[#070B16] to-transparent" />
            <div className="themed-fade-r-edge pointer-events-none absolute right-0 top-0 bottom-0 w-24 bg-gradient-to-l from-[#070B16] to-transparent" />
          </div>
        </Reveal>
      </div>
    </section>
  );
};

export default ImpactSnapshot;
